import React__default, { forwardRef, useRef } from 'react';
import { Dialog as Dialog$1, DialogTrigger, DialogPortal, DialogOverlay, DialogContent, DialogClose, DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/**
 *
 * Contains all the parts of a dialog.
 *
 */
const Root = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Dialog$1, props, children);
};
Root.displayName = "Root";
/**
 *
 * The button that opens the dialog.
 *
 */
const Trigger = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogTrigger, _extends({
    ref: innerRef
  }, props), children);
});

/**
 *
 * When used, portals your overlay and content parts into the `body`.
 *
 */
const Portal = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DialogPortal, props, children);
};
Portal.displayName = "Portal";
/**
 *
 * A layer that covers the inert portion of the view when the dialog is open.
 *
 */
const Overlay = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogOverlay, _extends({
    ref: innerRef
  }, props), children);
});

/**
 *
 * A layer that covers the inert portion of the view when the dialog is open.
 *
 */
const Content = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogContent, _extends({
    ref: innerRef
  }, props), children);
});

/**
 * The button that closes the dialog.
 */
const Close = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogClose, _extends({
    ref: innerRef
  }, props), children);
});

/**
 * An accessible title to be announced when the dialog is opened.
 */
const Title = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogTitle, _extends({
    ref: innerRef
  }, props), children);
});

/**
 * An optional accessible description to be announced when the dialog is opened.
 */
const Description = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DialogDescription, _extends({
    ref: innerRef
  }, props), children);
});
// exports
Root.displayName = 'Dialog';
Trigger.displayName = 'Trigger';
Portal.displayName = 'Portal';
Overlay.displayName = 'Overlay';
Content.displayName = 'Content';
Close.displayName = 'Close';
Title.displayName = 'Title';
Description.displayName = 'Description';

/** A window overlaid on either the primary window or another dialog window, rendering the content underneath inert. */
var Dialog = Object.assign(Root, {
  Trigger,
  Portal,
  Overlay,
  Content,
  Close,
  Title,
  Description
});

export { Dialog as default };
